// SzenarienView.tsx — #szenarien: the Szenario-Bibliothek + Export/Import (Feature 3).
// A named library (localStorage) beyond the Cockpit scenario list: save the current
// scenario under a name, list/load/rename/delete saved entries; export the current
// scenario as JSON or a shareable link; export the last computed result as CSV; import a
// pasted/uploaded scenario JSON. All parsing/serialisation logic lives in
// lib/scenarioIO.ts, lib/library.ts, lib/csv.ts, lib/share.ts (unit-tested) — this
// component is a thin renderer + DOM glue (downloads, clipboard), same split FundManager
// established for the Daten view's "add a fund" flow. "Load into a slot" is generalized
// to "load into any of the N Cockpit scenarios, or as a brand-new one" — see
// onLoadScenario/onLoadScenarioAsNew below (the N-scenario model, lib/storage.ts).
import { useState } from 'preact/hooks';
import type { ScenarioState } from '../lib/scenario.js';
import type { RunResult } from '../lib/scenario.js';
import type { PersistedState } from '../lib/storage.js';
import {
  addToLibrary,
  loadLibrary,
  removeFromLibrary,
  renameInLibrary,
  saveLibrary,
} from '../lib/library.js';
import type { LibraryEntry } from '../lib/library.js';
import { deserializeScenario, serializeScenario } from '../lib/scenarioIO.js';
import type { ScenarioImportResult } from '../lib/scenarioIO.js';
import { buildShareUrl } from '../lib/share.js';
import { eventsToCsv, seriesToCsv } from '../lib/csv.js';
import { formatDateTimeDe } from '../lib/format.js';
import { Hint } from '../components/Hint.js';

export interface SzenarienViewProps {
  scenarioState: PersistedState;
  results: Record<string, RunResult>;
  onLoadScenario: (id: string, scenario: ScenarioState) => void;
  onLoadScenarioAsNew: (scenario: ScenarioState) => void;
}

function downloadFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function safeFilenamePart(label: string): string {
  return label.replace(/[^a-z0-9äöüß]+/gi, '_').replace(/^_+|_+$/g, '') || 'szenario';
}

/** Renders "→ <Name>" buttons for every existing Cockpit scenario, plus one "Als neues
 * Szenario" button — the shared "load this ScenarioState somewhere" control used by the
 * library list, the import panel and (in app.tsx) the shared-link banner. */
function LoadTargetButtons({
  scenarios,
  onLoadInto,
  onLoadAsNew,
}: {
  scenarios: PersistedState['scenarios'];
  onLoadInto: (id: string) => void;
  onLoadAsNew: () => void;
}) {
  return (
    <span class="btn-row">
      {scenarios.map((entry) => (
        <button key={entry.id} type="button" class="btn" onClick={() => onLoadInto(entry.id)}>
          → {entry.scenario.label}
        </button>
      ))}
      <button type="button" class="btn" onClick={onLoadAsNew}>
        Als neues Szenario
      </button>
    </span>
  );
}

export function SzenarienView({ scenarioState, results, onLoadScenario, onLoadScenarioAsNew }: SzenarienViewProps) {
  const [library, setLibrary] = useState<LibraryEntry[]>(() => loadLibrary());
  const [sourceId, setSourceId] = useState<string>(scenarioState.activeId);
  const [saveName, setSaveName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [importText, setImportText] = useState('');
  const [importResult, setImportResult] = useState<ScenarioImportResult | null>(null);

  const sourceEntry = scenarioState.scenarios.find((e) => e.id === sourceId) ?? scenarioState.scenarios[0]!;
  const sourceScenario = sourceEntry.scenario;

  function persist(next: LibraryEntry[]) {
    setLibrary(next);
    saveLibrary(next);
  }

  function handleSave() {
    if (saveName.trim() === '') return;
    persist(addToLibrary(library, saveName, sourceScenario));
    setSaveName('');
  }

  function handleRenameStart(entry: LibraryEntry) {
    setRenamingId(entry.id);
    setRenameValue(entry.name);
  }

  function handleRenameConfirm(id: string) {
    persist(renameInLibrary(library, id, renameValue));
    setRenamingId(null);
  }

  function handleDelete(id: string) {
    persist(removeFromLibrary(library, id));
    setDeleteConfirmId(null);
  }

  function handleExportJson() {
    const json = serializeScenario(sourceScenario);
    downloadFile(`${safeFilenamePart(sourceScenario.label)}.json`, json, 'application/json');
  }

  function handleBuildShareLink() {
    setShareUrl(buildShareUrl(sourceScenario));
    setCopyStatus('idle');
  }

  async function handleCopyShareLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
  }

  function handleExportCsv(id: string, label: string, kind: 'series' | 'events') {
    const run = results[id];
    if (!run?.result) return;
    const safe = safeFilenamePart(label);
    if (kind === 'series') {
      downloadFile(`${safe}-verlauf.csv`, seriesToCsv(run.result.series), 'text/csv;charset=utf-8');
    } else {
      downloadFile(`${safe}-ereignisse.csv`, eventsToCsv(run.result.events), 'text/csv;charset=utf-8');
    }
  }

  function handleImportFile(e: Event) {
    const file = (e.currentTarget as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setImportText(text);
      setImportResult(deserializeScenario(text));
    };
    reader.readAsText(file);
  }

  function handleValidateImport() {
    setImportResult(deserializeScenario(importText));
  }

  function handleImportLoad(id: string) {
    if (importResult?.ok) {
      onLoadScenario(id, importResult.scenario);
      setImportResult(null);
      setImportText('');
    }
  }

  function handleImportLoadAsNew() {
    if (importResult?.ok) {
      onLoadScenarioAsNew(importResult.scenario);
      setImportResult(null);
      setImportText('');
    }
  }

  return (
    <div>
      <div class="card">
        <h2>Szenario-Bibliothek</h2>
        <Hint>
          <p class="field-hint">
            Speichere Szenarien über die Cockpit-Liste hinaus unter einem Namen, lade sie
            später wieder in ein bestehendes oder neues Szenario, exportiere sie als Datei
            oder Link, oder importiere ein zuvor exportiertes Szenario.
          </p>
        </Hint>

        <div class="field">
          <span class="field-label">Quelle</span>
          <div class="segmented segmented-scroll" role="group" aria-label="Quell-Szenario">
            {scenarioState.scenarios.map((entry) => (
              <button
                key={entry.id}
                type="button"
                class="chip"
                aria-pressed={sourceId === entry.id}
                onClick={() => setSourceId(entry.id)}
              >
                {entry.scenario.label}
              </button>
            ))}
          </div>
          <Hint>
            <p class="field-hint">Wirkt auf „Speichern“, „Als JSON exportieren“ und „Teilen-Link“ unten.</p>
          </Hint>
        </div>

        <div class="field">
          <label for="library-save-name">Name für die Bibliothek</label>
          <input
            id="library-save-name"
            type="text"
            value={saveName}
            onInput={(e) => setSaveName((e.currentTarget as HTMLInputElement).value)}
            placeholder="z. B. Konservativ 20 Jahre"
          />
        </div>
        <button type="button" class="btn btn-primary btn-block" onClick={handleSave} disabled={saveName.trim() === ''}>
          In Bibliothek speichern
        </button>

        <div class="btn-row" style={{ marginTop: '0.6rem' }}>
          <button type="button" class="btn" onClick={handleExportJson}>
            Als JSON exportieren
          </button>
          <button type="button" class="btn" onClick={handleBuildShareLink}>
            Teilen-Link erzeugen
          </button>
        </div>

        {shareUrl ? (
          <div class="field" style={{ marginTop: '0.6rem' }}>
            <label for="share-url-output">Teilen-Link</label>
            <input
              id="share-url-output"
              class="input-wide"
              type="text"
              readonly
              value={shareUrl}
              onClick={(e) => (e.currentTarget as HTMLInputElement).select()}
            />
            <div class="btn-row" style={{ marginTop: '0.4rem' }}>
              <button type="button" class="btn" onClick={() => void handleCopyShareLink()}>
                Link kopieren
              </button>
            </div>
            {copyStatus === 'copied' ? <p class="field-hint">Kopiert.</p> : null}
            {copyStatus === 'failed' ? <p class="field-hint">Kopieren fehlgeschlagen — Link manuell markieren.</p> : null}
          </div>
        ) : null}
      </div>

      <div class="card">
        <h2>Gespeicherte Szenarien</h2>
        {library.length === 0 ? (
          <div class="empty-state">Noch keine Szenarien in der Bibliothek gespeichert.</div>
        ) : (
          <ul class="source-list">
            {library.map((entry) => (
              <li key={entry.id} class="source-row" style={{ flexWrap: 'wrap' }}>
                {renamingId === entry.id ? (
                  <span class="btn-row" style={{ flex: '1 1 100%' }}>
                    <input
                      type="text"
                      value={renameValue}
                      onInput={(e) => setRenameValue((e.currentTarget as HTMLInputElement).value)}
                      aria-label={`Neuer Name für ${entry.name}`}
                      style={{ flex: '1 1 auto', minWidth: 0 }}
                    />
                    <button type="button" class="btn" onClick={() => handleRenameConfirm(entry.id)}>
                      OK
                    </button>
                    <button type="button" class="btn" onClick={() => setRenamingId(null)}>
                      Abbrechen
                    </button>
                  </span>
                ) : (
                  <>
                    <span>
                      {entry.name} <span class="faint">({formatDateTimeDe(entry.savedAt)})</span>
                    </span>
                    <LoadTargetButtons
                      scenarios={scenarioState.scenarios}
                      onLoadInto={(id) => onLoadScenario(id, entry.scenario)}
                      onLoadAsNew={() => onLoadScenarioAsNew(entry.scenario)}
                    />
                    <span class="btn-row">
                      <button type="button" class="btn" onClick={() => handleRenameStart(entry)}>
                        Umbenennen
                      </button>
                      {deleteConfirmId === entry.id ? (
                        <button type="button" class="btn" onClick={() => handleDelete(entry.id)}>
                          Wirklich löschen
                        </button>
                      ) : (
                        <button type="button" class="btn" onClick={() => setDeleteConfirmId(entry.id)}>
                          Löschen
                        </button>
                      )}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div class="card">
        <h2>Ergebnis-Export (CSV)</h2>
        <Hint>
          <p class="field-hint">
            Exportiert die zuletzt berechnete Zeitreihe bzw. Ereignisliste des jeweiligen
            Szenarios (Komma durch Semikolon ersetzt, Dezimalkomma — für Excel im
            de-AT-Gebietsschema).
          </p>
        </Hint>
        {scenarioState.scenarios.map((entry) => (
          <div key={entry.id} class="field">
            <span class="field-label">{entry.scenario.label}</span>
            {results[entry.id]?.result ? (
              <div class="btn-row">
                <button type="button" class="btn" onClick={() => handleExportCsv(entry.id, entry.scenario.label, 'series')}>
                  Verlauf als CSV
                </button>
                <button type="button" class="btn" onClick={() => handleExportCsv(entry.id, entry.scenario.label, 'events')}>
                  Ereignisse als CSV
                </button>
              </div>
            ) : (
              <p class="field-hint">Noch kein Ergebnis berechnet (im Cockpit „Berechnen“ drücken).</p>
            )}
          </div>
        ))}
      </div>

      <div class="card">
        <h2>Szenario importieren</h2>
        <Hint>
          <p class="field-hint">Datei laden oder JSON einfügen, prüfen, dann laden.</p>
        </Hint>
        <div class="field">
          <label for="import-file">Datei</label>
          <input id="import-file" type="file" accept="application/json,.json" onChange={handleImportFile} />
        </div>
        <div class="field">
          <label for="import-text">JSON</label>
          <textarea
            id="import-text"
            rows={6}
            value={importText}
            onInput={(e) => setImportText((e.currentTarget as HTMLTextAreaElement).value)}
            placeholder="Hier Szenario-JSON einfügen…"
            style={{
              width: '100%',
              minHeight: '6rem',
              background: 'var(--bg-input)',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text)',
              padding: '0.5rem 0.65rem',
              fontFamily: 'inherit',
              fontSize: '0.85rem',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <button type="button" class="btn btn-block" onClick={handleValidateImport} disabled={importText.trim() === ''}>
          Prüfen
        </button>

        {importResult && !importResult.ok ? (
          <div class="banner banner-error" role="alert" style={{ marginTop: '0.6rem' }}>
            <strong>Import fehlgeschlagen</strong>
            <span>{importResult.error}</span>
          </div>
        ) : null}

        {importResult?.ok ? (
          <div class="banner banner-info" style={{ marginTop: '0.6rem' }}>
            <strong>Gültiges Szenario erkannt: {importResult.scenario.label}</strong>
            <span>
              Modus: {importResult.scenario.mode === 'forecast' ? 'Prognose' : 'Backtest'}. Lädt nicht automatisch —
              bitte Ziel wählen.
            </span>
            <div style={{ marginTop: '0.5rem' }}>
              <LoadTargetButtons
                scenarios={scenarioState.scenarios}
                onLoadInto={handleImportLoad}
                onLoadAsNew={handleImportLoadAsNew}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
